import { useState } from 'react';
import { Icon, type IconName } from './Icon';

export const canonicalStateKinds = [
  'loading',
  'empty',
  'error',
  'forbidden',
  'offline',
  'success',
] as const;
export type CanonicalStateKind = (typeof canonicalStateKinds)[number];

export type CanonicalStateDefinition = Readonly<{
  kind: CanonicalStateKind;
  label: string;
  title: string;
  message: string;
  actionLabel?: string;
}>;

export const canonicalStates: readonly CanonicalStateDefinition[] = [
  {
    kind: 'loading',
    label: 'Carga',
    title: 'Cargando información',
    message: 'Espera un momento mientras preparamos el contenido solicitado.',
  },
  {
    kind: 'empty',
    label: 'Vacío',
    title: 'Aún no hay contenido',
    message: 'Cuando exista información disponible, aparecerá organizada en este espacio.',
    actionLabel: 'Volver a revisar',
  },
  {
    kind: 'error',
    label: 'Error',
    title: 'No pudimos completar la solicitud',
    message: 'Ocurrió un problema inesperado. Intenta nuevamente o informa el incidente.',
    actionLabel: 'Reintentar',
  },
  {
    kind: 'forbidden',
    label: 'Sin permiso',
    title: 'No tienes acceso a este contenido',
    message: 'Tu sesión está activa, pero no cuenta con el permiso necesario para continuar.',
    actionLabel: 'Volver a un lugar seguro',
  },
  {
    kind: 'offline',
    label: 'Offline',
    title: 'Sin conexión',
    message: 'El shell sigue disponible, pero esta información necesita conexión con el servidor.',
    actionLabel: 'Comprobar conexión',
  },
  {
    kind: 'success',
    label: 'Éxito',
    title: 'Operación completada',
    message: 'La acción terminó correctamente y el resultado ya está disponible.',
    actionLabel: 'Ver confirmación',
  },
] as const;

const stateIcons: Record<CanonicalStateKind, IconName> = {
  loading: 'loading',
  empty: 'empty',
  error: 'error',
  forbidden: 'lock',
  offline: 'offline',
  success: 'check',
};

export function StatePanel({
  state,
  onAction,
}: {
  state: CanonicalStateDefinition;
  onAction?: (kind: CanonicalStateKind) => void;
}) {
  const liveRole =
    state.kind === 'error' ? 'alert' : state.kind === 'loading' ? 'status' : undefined;
  return (
    <section
      className={`ui-state-panel ui-state-panel--${state.kind}`}
      aria-labelledby={`state-${state.kind}-title`}
      role={liveRole}
      aria-live={state.kind === 'loading' ? 'polite' : undefined}
      aria-busy={state.kind === 'loading' ? true : undefined}
    >
      <span className="ui-state-icon" aria-hidden="true">
        <Icon name={stateIcons[state.kind]} />
      </span>
      <p className="ui-state-eyebrow">Estado · {state.label}</p>
      <h2 id={`state-${state.kind}-title`}>{state.title}</h2>
      <p className="ui-state-message">{state.message}</p>
      {state.actionLabel ? (
        <button className="ui-state-action" type="button" onClick={() => onAction?.(state.kind)}>
          {state.actionLabel}
        </button>
      ) : (
        <span className="ui-state-progress">Procesando…</span>
      )}
    </section>
  );
}

export function StateGallery({ initialState = 'loading' }: { initialState?: CanonicalStateKind }) {
  const [selected, setSelected] = useState<CanonicalStateKind>(initialState);
  const [feedback, setFeedback] = useState('');
  const state = canonicalStates.find((item) => item.kind === selected) ?? canonicalStates[0]!;
  const select = (kind: CanonicalStateKind) => {
    setSelected(kind);
    setFeedback('');
  };
  const act = (kind: CanonicalStateKind) => {
    const label = canonicalStates.find((item) => item.kind === kind)?.label ?? kind;
    setFeedback(`Acción de ${label.toLowerCase()} comprobada.`);
  };
  return (
    <div className="ui-state-gallery">
      <div className="ui-state-switcher" aria-label="Inspeccionar estados canónicos">
        {canonicalStates.map((item) => (
          <button
            key={item.kind}
            type="button"
            aria-pressed={selected === item.kind}
            onClick={() => select(item.kind)}
          >
            <Icon name={stateIcons[item.kind]} />
            {item.label}
          </button>
        ))}
      </div>
      <StatePanel state={state} onAction={act} />
      <p className="ui-state-feedback" aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
